import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL || "";

export default function ProfileDialog({ open, onOpenChange }) {
    const { user } = useAuth();
    const fileRef = useRef(null);
    const [reload, setReload] = useState(0);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { if (!open) setReload(0); }, [open]);

    const upload = async () => {
        const f = fileRef.current?.files?.[0];
        if (!f) return toast.error("Selecciona una imagen PNG");
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", f);
            await api.post(`/professionals/${user.id}/signature`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Firma actualizada");
            setReload(Date.now());
            if (fileRef.current) fileRef.current.value = "";
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo firma");
        } finally { setUploading(false); }
    };
    const remove = async () => {
        if (!window.confirm("¿Eliminar tu firma?")) return;
        try {
            await api.delete(`/professionals/${user.id}/signature`);
            toast.success("Firma eliminada");
            setReload(Date.now());
        } catch (e) { toast.error("Error"); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="profile-dialog">
                <DialogHeader><DialogTitle>Mi firma digital</DialogTitle></DialogHeader>
                <div className="space-y-4 text-sm">
                    <p className="text-muted-foreground">
                        Sube una imagen PNG con fondo transparente de tu firma. Se estampará automáticamente en los informes que firmes.
                    </p>
                    <div className="rounded-md border border-border bg-secondary/40 p-4">
                        <div className="text-xs uppercase-label mb-2">Firma actual</div>
                        <img
                            key={reload}
                            src={`${backend}/api/professionals/${user?.id}/signature?t=${reload}`}
                            alt="Mi firma"
                            className="max-h-28 bg-white rounded border p-2"
                            data-testid="my-signature-preview"
                            onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "block"; }}
                        />
                        <div className="text-xs text-red-600 mt-1" style={{ display: "none" }}>
                            Aún no has subido ninguna firma.
                        </div>
                    </div>
                    <div>
                        <Label className="uppercase-label mb-1 block">Nueva firma (PNG/JPG/WEBP)</Label>
                        <Input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" data-testid="signature-file" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={remove} data-testid="signature-delete">
                        <Trash2 size={14} className="mr-1" /> Eliminar firma
                    </Button>
                    <Button className="btn-cta" onClick={upload} disabled={uploading} data-testid="signature-upload">
                        <Upload size={14} className="mr-1" /> {uploading ? "Subiendo…" : "Subir firma"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
